import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, CheckCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

type AuthView = 'select' | 'magic-link' | 'credentials';

const NOTIFY_SLACK_URL = `${import.meta.env.VITE_SUPABASE_URL || "https://kiwaauwhjypkikegymro.supabase.co"}/functions/v1/notify-login-slack`;

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [view, setView] = useState<AuthView>('select');
  const navigate = useNavigate();
  const { toast } = useToast();

  async function notifySlackLogin(userEmail: string) {
    try {
      console.log("Attempting to notify Slack of login:", userEmail);
      console.log("Using URL:", NOTIFY_SLACK_URL);
      
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      if (!accessToken) {
        console.error("No access token available for API call");
        return;
      }
      
      const response = await fetch(NOTIFY_SLACK_URL, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          record: { email: userEmail },
          event: "SIGNED_IN",
          isInitialLogin: true,
        }),
      });
      
      const result = await response.json();
      console.log("Slack notification response:", result);
      
      if (!response.ok) {
        throw new Error(`Failed to notify Slack: ${JSON.stringify(result)}`);
      }
    } catch (err) {
      console.error("Failed to notify Slack of login:", err);
    }
  }

  async function updateLastLoginTimestamp(userId: string) {
    try {
      console.log("Updating last login timestamp for user:", userId);
      
      const { error } = await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId);
      
      if (error) {
        console.error("Error updating last login timestamp:", error);
      } else {
        console.log("Last login timestamp updated successfully");
      }
    } catch (err) {
      console.error("Failed to update last login timestamp:", err);
    }
  }

  useEffect(() => {
    const handleAuthToken = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const access_token = hashParams.get('access_token');
      const refresh_token = hashParams.get('refresh_token');
      
      if (access_token && refresh_token) {
        try {
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token
          });
          if (error) throw error;

          const { data: currentUserData } = await supabase.auth.getUser();
          const userEmail = currentUserData?.user?.email || "";
          const userId = currentUserData?.user?.id;
          
          if (userId) {
            await updateLastLoginTimestamp(userId);
          }

          await notifySlackLogin(userEmail);

          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .single();

          if (!profile?.first_name || !profile?.last_name) {
            navigate("/profile");
          } else {
            navigate("/intro");
          }
        } catch (error) {
          console.error("Error in auth flow:", error);
          toast({
            variant: "destructive",
            title: "Error al procesar el enlace",
            description: "Hubo un problema al procesar el enlace. Por favor, intente nuevamente.",
          });
        }
      }
    };

    handleAuthToken();
  }, [navigate]);

  const handleMagicLinkSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const normalizedEmail = email.toLowerCase().trim();

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (profileError) {
        console.error('Error checking profile:', profileError);
        toast({
          variant: "destructive",
          title: "Error al verificar usuario",
          description: "Hubo un problema al verificar el usuario. Por favor, intente nuevamente.",
        });
        setLoading(false);
        return;
      }

      if (!profile) {
        toast({
          variant: "destructive",
          title: "Usuario no registrado",
          description: "Este correo electrónico no está registrado en el sistema. Por favor, contacta al administrador.",
        });
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`
        }
      });

      if (error) {
        console.error('Sign in error:', error);
        throw error;
      }

      setEmailSent(true);
      toast({
        title: "¡Enlace enviado!",
        description: "Revisa tu correo electrónico para iniciar sesión.",
      });
    } catch (error: any) {
      console.error('Magic link sign in error:', error);
      toast({
        variant: "destructive",
        title: "Error al enviar el enlace",
        description: error.message || "Ocurrió un error al enviar el enlace de acceso",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialsSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) throw error;

      if (data.user?.id) {
        await updateLastLoginTimestamp(data.user.id);
      }

      if (rememberMe && data.session) {
        console.log("Remember me checked, session will persist for 30 days");
        try {
          const { error: refreshError } = await supabase.auth.refreshSession({
            refresh_token: data.session.refresh_token,
          });
          
          if (refreshError) {
            console.error("Error refreshing session after login:", refreshError);
          } else {
            console.log("Session extended for remember me");
          }
        } catch (refreshError) {
          console.error("Error extending session:", refreshError);
        }
      }

      console.log("Login successful, preparing to notify Slack");
      
      await notifySlackLogin(normalizedEmail);

      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', data.user.id)
        .single();

      if (!profile?.first_name || !profile?.last_name) {
        navigate("/profile");
      } else {
        navigate("/intro");
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      toast({
        variant: "destructive",
        title: "Error al iniciar sesión",
        description: "Usuario o contraseña incorrectos",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 relative">
      <header className="border-b border-gray-200 bg-white">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <a href="https://www.revenuehublatam.com" target="_blank" rel="noopener noreferrer">
            <img 
              src="/lovable-uploads/c8dc929a-2605-4fd5-ba4c-cd8a0a90ddda.png" 
              alt="Revenue Hub" 
              className="h-8" 
            />
          </a>
          <div className="text-sm text-gray-900">Para empresas que usen un CRM y tengan equipos de ventas</div>
        </div>
      </header>
      
      <main className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
                FocusFlow
              </span>
              <span className="block mt-2 text-white">Automatiza. Alinea. Vende más.</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Aumenta tus ventas hasta un <span className="text-blue-400 font-semibold">20%</span> en 3-6 meses optimizando procesos con FocusFlow.
            </p>
          </div>
          
          <div className="flex flex-col space-y-8">
            <div className="w-full max-w-md mx-auto">
              {view === 'select' && (
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h2 className="text-2xl font-bold mb-2 text-gray-800">Accede a tu cuenta</h2>
                  <p className="text-gray-600 mb-8">Elige tu método de acceso preferido</p>
                  
                  <div className="space-y-4">
                    <button 
                      onClick={() => setView('magic-link')}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg flex items-center justify-between transition-colors"
                    >
                      <span className="font-medium">Link mágico</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                    
                    <button 
                      onClick={() => setView('credentials')}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-lg flex items-center justify-between transition-colors"
                    >
                      <span className="font-medium">Usuario y contraseña</span>
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {view === 'magic-link' && (
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <div className="flex items-center mb-6">
                    <button 
                      onClick={() => setView('select')}
                      className="p-2 mr-2 rounded-full hover:bg-gray-100 text-gray-700 transition-colors"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800">Link mágico</h2>
                  </div>
                  
                  {emailSent ? (
                    <div className="text-center space-y-4">
                      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle className="h-8 w-8 text-green-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800">¡Enlace enviado!</h3>
                      <p className="text-gray-600">
                        Te hemos enviado un enlace de acceso a {email}. 
                        Por favor, revisa tu bandeja de entrada y sigue el enlace para iniciar sesión.
                      </p>
                      <button
                        type="button"
                        className="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium transition-colors"
                        onClick={() => {
                          setEmailSent(false);
                          setView('select');
                        }}
                      >
                        Usar otro método
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleMagicLinkSignIn} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email corporativo</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full p-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 outline-none"
                          placeholder="tu@empresa.com"
                          required
                        />
                      </div>
                      
                      <button 
                        type="submit"
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                        disabled={loading}
                      >
                        {loading ? "Enviando..." : "Enviar link de acceso"}
                      </button>
                    </form>
                  )}
                </div>
              )}
              
              {view === 'credentials' && (
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <div className="flex items-center mb-6">
                    <button 
                      onClick={() => setView('select')}
                      className="p-2 mr-2 rounded-full hover:bg-gray-100 text-gray-700 transition-colors"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800">Iniciar sesión</h2>
                  </div>
                  
                  <form onSubmit={handleCredentialsSignIn} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 outline-none"
                        placeholder="tu@empresa.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full p-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 outline-none"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="rememberMe" 
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked === true)}
                      />
                      <label
                        htmlFor="rememberMe"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-gray-700"
                      >
                        Mantener sesión iniciada por 30 días
                      </label>
                    </div>
                    
                    <button 
                      type="submit"
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                      disabled={loading}
                    >
                      {loading ? "Iniciando sesión..." : "Iniciar sesión"}
                    </button>
                  </form>
                </div>
              )}
            </div>
            
            <div className="w-full">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700">
                <h3 className="text-xl font-bold mb-6 text-blue-400">Transforma tu proceso de ventas</h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <div className="flex items-center mb-3">
                      <div className="bg-blue-500/20 rounded-full p-1 mr-3">
                        <CheckCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <h4 className="font-bold text-white">Visibilidad en tiempo real</h4>
                    </div>
                    <p className="text-gray-400 text-sm">Tableros personalizados que muestran exactamente dónde está cada oportunidad en el pipeline.</p>
                  </div>
                  
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <div className="flex items-center mb-3">
                      <div className="bg-blue-500/20 rounded-full p-1 mr-3">
                        <CheckCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <h4 className="font-bold text-white">Automatización inteligente</h4>
                    </div>
                    <p className="text-gray-400 text-sm">Reduce hasta 70% del tiempo en tareas administrativas para que tus vendedores se enfoquen en cerrar.</p>
                  </div>
                  
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <div className="flex items-center mb-3">
                      <div className="bg-blue-500/20 rounded-full p-1 mr-3">
                        <CheckCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <h4 className="font-bold text-white">Alineación de equipos</h4>
                    </div>
                    <p className="text-gray-400 text-sm">Conecta marketing y ventas con procesos claros que mejoran la colaboración y la conversión.</p>
                  </div>
                  
                  <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                    <div className="flex items-center mb-3">
                      <div className="bg-blue-500/20 rounded-full p-1 mr-3">
                        <CheckCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <h4 className="font-bold text-white">Estrategia escalable</h4>
                    </div>
                    <p className="text-gray-400 text-sm">Procesos estandarizados que permiten crecer sin perder control ni eficiencia.</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-900/40 to-blue-800/20 p-6 rounded-xl border border-blue-800/30">
                  <p className="text-gray-300 italic">
                    "Implementamos FocusFlow y en menos de 4 meses incrementamos nuestras conversiones en un 24%, con el mismo equipo y presupuesto. La visibilidad y automatización han sido transformadoras."
                  </p>
                  <div className="mt-4 font-medium text-blue-400">
                    — Gerente Comercial, empresa de tecnología
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <footer className="border-t border-gray-800 mt-16">
        <div className="container mx-auto px-6 py-6">
          <div className="text-center text-sm text-gray-500">
            Copyright © Revenue Hub Consulting SpA - Todos los derechos reservados
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Auth;
